import React, { useMemo } from 'react';

import { KeySystemLayout } from '@/components/sections/service/parts/parts-item/key-system-layout';
import { PartsGenuineItem } from '@/components/sections/service/parts/parts-item/parts-genuine-item';
import { PartsItemDetail } from '@/components/sections/service/parts/parts-item/parts-item-detail';
import { PartsNote } from '@/components/sections/service/parts/parts-item/parts-note';
import {
  PartsRemark,
  PartsRemarkItems,
} from '@/components/sections/service/parts/parts-item/parts-remark';
import { PartsTable } from '@/components/sections/service/parts/parts-item/parts-table';
import { Button } from '@/components/shared/buttons/button';
import { Box } from '@/components/shared/layout/box';
import { Grid } from '@/components/shared/layout/grid';
import { Image } from '@/components/shared/media/image';
import { styled } from '@/stitches.config';
import { IParts } from '@/types/shared/parts';

export const PartsInnerStyled = styled('div', {
  px: '$space-4',
  pt: '$space-12',
  pb: '$space-6',
  '@lg': {
    px: '$space-4',
    py: '$space-6',
  },
  variants: {
    size: {
      small: {
        '@sm': {
          px: '$space-4',
          py: '$space-6',
        },
      },
    },
  },
});

export const ImageWrapStyled = styled('div', {
  '& > *': {
    height: 'auto',
  },
});

const PartsInnerGridStyled = styled(Grid, {
  gridTemplateColumns: '30% auto',
  columnGap: '$space-4',
  '@sm': {
    gridTemplateColumns: '170px auto',
    columnGap: '$space-9',
  },
  variants: {
    size: {
      small: {
        gridTemplateColumns: '30% auto',
        columnGap: '$space-4',
        '@sm': {
          gridTemplateColumns: '130px auto',
          columnGap: '$space-4',
        },
      },
    },
  },
});

interface PartsItemProps {
  parts: IParts;
  size?: 'small' | undefined;
  dataTest?: string;
}

const PartsItem: React.FC<PartsItemProps> = ({
  size,
  dataTest = 'popup_partsDetail',
  ...props
}: PartsItemProps) => {
  const isSmall = size === 'small';

  const hasRemark = useMemo(() => {
    return (
      props.parts?.remark || props.parts?.remarkStick || props.parts?.remark2
    );
  }, [props.parts?.remark, props.parts?.remarkStick, props.parts?.remark2]);

  return (
    <>
      {props.parts.keySystemLayout ? (
        <KeySystemLayout parts={props.parts} dataTest={dataTest} />
      ) : (
        <>
          <PartsInnerStyled size={size}>
            <PartsInnerGridStyled alignItems="center" size={size}>
              {!!props.parts.image?.src && (
                <ImageWrapStyled>
                  <Image
                    src={props.parts.image.src}
                    alt={props.parts.image.alt}
                    width={600}
                    height={750}
                    layout="responsive"
                    aspectRatio="4by5"
                    objectFit="contain"
                    objectPosition="bottom"
                    sources={[
                      {
                        srcset: props.parts.image.src,
                        options: {
                          width: 256,
                          crop: '4:5',
                        },
                      },
                    ]}
                  />
                </ImageWrapStyled>
              )}

              <Grid
                css={{
                  alignSelf: 'center',
                  rowGap: '$space-3',
                  ...(!isSmall && {
                    '@sm': { rowGap: '$space-4' },
                  }),
                }}
              >
                <PartsItemDetail
                  parts={props.parts}
                  size={size}
                  dataTest={dataTest}
                />

                {!!props.parts.genuinePartsItem &&
                  props.parts.genuinePartsItem.length > 0 && (
                    <PartsGenuineItem
                      genuinePartsItem={props.parts.genuinePartsItem}
                      size={size}
                      dataTest={dataTest}
                    />
                  )}

                {!!hasRemark && (
                  <PartsRemark
                    parts={props.parts}
                    size={size}
                    dataTest={dataTest}
                  />
                )}

                {!!props.parts.remarkItems &&
                  props.parts.remarkItems.length > 0 && (
                    <PartsRemarkItems
                      items={props.parts.remarkItems}
                      size={size}
                      dataTest={dataTest}
                    />
                  )}

                {!!props.parts.button && (
                  <Box>
                    <Button
                      label={props.parts.button.label}
                      size="md"
                      border
                      variant="button-gray"
                      href={props.parts.button.url}
                      target={props.parts.button.target}
                      test={{
                        dataTest: dataTest + '_button_cta',
                      }}
                    />
                  </Box>
                )}
              </Grid>
            </PartsInnerGridStyled>
          </PartsInnerStyled>

          {!!props.parts.table && (
            <PartsTable size={size} table={props?.parts?.table} />
          )}
          {!!props.parts?.note && (
            <PartsNote dataTest={dataTest} note={props.parts?.note} />
          )}
        </>
      )}
    </>
  );
};

PartsItem.displayName = 'SectionPartsItem';
export default PartsItem;
